import axios from 'axios';
import events from 'Assets/js/components/events';
import rateLimit from 'axios-rate-limit';
import globalTheme from 'Assets/js/components/globalTheme';

// @todo This is set in different modules: isn't it better to do it once, e.g. in main.js?
const http = rateLimit(axios.create(), {
  maxRequests: 6,
  perMilliseconds: 1000,
  maxRPS: 6
});

const triggerErrorLog = (error) => {
  axios.post('/wp-json/teams/v1/send', {
    error: { 'service: ': 'stockService', error }
  });
};

const themeObject = () => window.global_theme_object || {};

/**
 * The market the customer is browsing, as a Shopify CountryCode, or null when we
 * cannot be sure of it.
 *
 * `currency.country` is the two letter code (`AU`), while `currency.country_code`
 * is a locale (`en_AU`), so only the former can be used here. The market list
 * confusingly names its two letter code `country_code`, and it is the same list
 * the `validMarkets` Vuex getter uses.
 *
 * @return {string|null}
 */
const marketCountryCode = () => {
  if (!themeObject().feature_enabled?.market_catalog_check) {
    return null;
  }

  const country = themeObject().currency?.country;

  if (!country || !/^[A-Z]{2}$/.test(country)) {
    return null;
  }

  const markets = Object.values(themeObject().markets?.markets || {});

  return markets.some((market) => market.country_code === country)
    ? country
    : null;
};

const STOCK_FIELDS = `product( id: $id ) {
              availableForSale
              totalInventory
              variants(first: 10){
                  edges{
                      node{
                          id
                          availableForSale
                          quantityAvailable
                      }
                  }
              }
            }`;

/**
 * The Storefront API is catalog scoped, so a product outside the market's catalog
 * resolves to null under @inContext. That is the only way to see the restriction:
 * Shopify returns a price for it either way. See OSS-2671.
 *
 * @param {string|null} country
 * @return {string}
 */
const stockQuery = (country) =>
  country
    ? `query product($id: ID!, $country: CountryCode!) @inContext(country: $country) {
            ${STOCK_FIELDS}
        }`
    : `query product($id: ID!){
            ${STOCK_FIELDS}
        }`;

/**
 *
 * @return {*}
 * @constructor
 */
function StockServiceConstructor() {
  const shopifySettings = globalTheme.shopify();
  const url = `https://${shopifySettings.url}/api/${shopifySettings.api_version}/graphql.json`;
  const storeFrontAccessToken = shopifySettings.store_front_access_token;

  /**
   * @param id
   * @param {function|undefined} callback
   * @param {function|undefined} failureCallback
   * @param {function|undefined} unavailableInMarketCallback Called instead of
   *        callback when the product is not in the current market's catalog.
   */
  const checkStock = (
    id,
    callback = undefined,
    failureCallback = undefined,
    unavailableInMarketCallback = undefined
  ) => {
    if (id === 'gid://shopify/Product/') {
      return;
    }

    if (!id) {
      // eslint-disable-next-line no-console
      console.error('stockService.checkStock failed: invalid variant ID', {
        id
      });
    }

    // eslint-disable-next-line no-console
    // console.log('stockService.checkStock, variant ID', id);

    const country = marketCountryCode();

    const data = JSON.stringify({
      query: stockQuery(country),
      variables: country ? { id, country } : { id }
    });

    const config = {
      method: 'post',
      url,
      headers: {
        'X-Shopify-Storefront-Access-Token': storeFrontAccessToken,
        'Content-Type': 'application/json'
      },
      data
    };

    http(config).then(
      (response) => {
        if (response.data.errors) {
          triggerErrorLog(response.data.errors);
        }

        const { product } = response.data.data;

        // Under a country context, a null product means the product is not in
        // that market's catalog, so Shopify will refuse to deliver it there.
        if (country && product === null) {
          if (typeof unavailableInMarketCallback === 'function') {
            unavailableInMarketCallback();
          }

          return;
        }

        if (typeof callback === 'function' && product !== undefined) {
          callback(product);
        }
      },
      (rejected) => {
        if (typeof failureCallback === 'function') {
          failureCallback(rejected);
        }
      }
    );
  };

  return {
    checkStock,
    events: () => events
  };
}

// eslint-disable-next-line func-names
const stockService = (function () {
  // Singleton instance
  const instance = new StockServiceConstructor();
  return {
    getInstance: () => instance
  };
})();

export default stockService;
